/**
 * Firebase Initialization Module
 * Handles Firebase setup for both native and web platforms
 * Provides a clean abstraction layer for platform differences
 */

// CRITICAL: Silence Firebase logger FIRST, before anything else loads
import '~services/FirebaseDebug/firebaseSilencer';
// Then import pre-patch and early suppression
import '~services/FirebaseDebug/prePatch';
import '~services/FirebaseDebug/earlySuppress';

import { Platform } from 'react-native';
import { FIREBASE_CONFIG } from '~services/FirebaseConfig';
import FirebaseDiagnostics from '~services/FirebaseDebug/diagnostics';
import setupFirebaseLogSuppression from '~services/FirebaseDebug/suppressFirebaseErrors';
import patchFirebaseLogger from '~services/FirebaseDebug/firebaseLoggerPatch';
import silenceFirebaseSDK from '~services/FirebaseDebug/silenceFirebaseSDK';

// Track initialization state
let isInitialized = false;
let initializationError: Error | null = null;

/**
 * Detects if running on web platform
 */
const isWebPlatform = (): boolean => {
  // Check multiple ways to detect web platform
  return (
    Platform.OS === 'web' ||
    typeof window !== 'undefined' ||
    process.env.WEB === 'true' ||
    typeof navigator !== 'undefined'
  );
};

/**
 * Platform-safe Firestore import
 * Returns actual Firebase Firestore on web, stub on native (if needed)
 */
export const getFirestore = () => {
  if (isWebPlatform()) {
    // Use Firebase Web SDK
    try {
      const { getFirestore: webGetFirestore } = require('firebase/firestore');
      console.log('✅ [FIREBASE] Using Firebase Web SDK (firestore)');
      return webGetFirestore;
    } catch (error) {
      console.warn('⚠️ [FIREBASE] Firebase Web SDK not available, using stub');
      // Fallback to web stub
      return require('~services/web/firestoreStub').default;
    }
  } else {
    // Native platform - use react-native-firebase
    const firestore = require('@react-native-firebase/firestore').default;
    console.log('✅ [FIREBASE] Using React Native Firebase (firestore)');
    return firestore;
  }
};

/**
 * Platform-safe Firestore instance getter
 * This matches the interface expected by native code
 */
export const getFirestoreInstance = () => {
  if (isWebPlatform()) {
    console.log('🌐 [FIREBASE] Returning web firestore stub instance');
    const stub = require('~services/web/firestoreStub').default;
    // Return a callable function to match native API
    return stub;
  } else {
    // Native - return actual instance
    const firestore = require('@react-native-firebase/firestore').default;
    console.log('📱 [FIREBASE] Returning native firestore instance');
    return firestore();
  }
};

/**
 * Get Firebase Auth
 */
export const getAuth = () => {
  if (isWebPlatform()) {
    try {
      const { getAuth: webGetAuth } = require('firebase/auth');
      console.log('✅ [FIREBASE] Using Firebase Web SDK (auth)');
      return webGetAuth;
    } catch (error) {
      console.warn('⚠️ [FIREBASE] Firebase Web SDK Auth not available, using stub');
      return require('~services/web/firebaseAuthStub').default;
    }
  } else {
    const auth = require('@react-native-firebase/auth').default;
    console.log('✅ [FIREBASE] Using React Native Firebase (auth)');
    return auth;
  }
};

/**
 * Get Firebase Messaging
 */
export const getMessaging = () => {
  if (isWebPlatform()) {
    try {
      const { getMessaging: webGetMessaging } = require('firebase/messaging');
      console.log('✅ [FIREBASE] Using Firebase Web SDK (messaging)');
      return webGetMessaging;
    } catch (error) {
      console.warn('⚠️ [FIREBASE] Firebase Web SDK Messaging not available, using stub');
      return require('~services/web/messagingStub').default;
    }
  } else {
    const messaging = require('@react-native-firebase/messaging').default;
    console.log('✅ [FIREBASE] Using React Native Firebase (messaging)');
    return messaging;
  }
};

/**
 * Get Firebase Analytics
 */
export const getAnalytics = () => {
  if (isWebPlatform()) {
    try {
      // For web, our analyticsStub.ts is now a real implementation
      // and is already aliased in webpack.config.js
      return require('~services/web/analyticsStub').default;
    } catch (error) {
      console.warn('⚠️ [FIREBASE] Analytics implementation not available');
      return () => ({
        logEvent: () => Promise.resolve(),
        setUserId: () => Promise.resolve(),
        setUserProperties: () => Promise.resolve(),
        logScreenView: () => Promise.resolve(),
      });
    }
  } else {
    try {
      const analytics = require('@react-native-firebase/analytics').default;
      console.log('✅ [FIREBASE] Using React Native Firebase (analytics)');
      return analytics;
    } catch (error) {
      console.error('❌ [FIREBASE] Failed to load native Firebase Analytics:', error);
      return () => ({
        logEvent: () => Promise.resolve(),
        setUserId: () => Promise.resolve(),
        setUserProperties: () => Promise.resolve(),
        logScreenView: () => Promise.resolve(),
      });
    }
  }
};

/**
 * Initialize Firebase for the current platform
 * Should be called once at app startup
 */
export const initializeFirebase = async (): Promise<boolean> => {
  if (isInitialized) {
    console.log('ℹ️ [FIREBASE] Already initialized');
    return !initializationError;
  }

  try {
    const isWeb = isWebPlatform();
    console.log(`🔥 [FIREBASE] Initializing Firebase (platform: ${isWeb ? 'WEB' : 'NATIVE'})`);

    if (isWeb) {
      // Silence Firebase SDK logging FIRST, before any other initialization
      silenceFirebaseSDK();
      
      // Suppress Firebase connection error logs
      setupFirebaseLogSuppression();
      patchFirebaseLogger();

      console.log('🌐 [FIREBASE] Initializing Firebase Web SDK...');
      try {
        // Try to initialize Firebase Web SDK
        const { initializeApp, getApps } = require('firebase/app');
        
        if (getApps().length > 0) {
          console.log('✅ [FIREBASE] Firebase app already initialized');
        } else {
          const config = FIREBASE_CONFIG;

          console.log('🔑 [FIREBASE] Firebase config (web):', {
            apiKey: config.apiKey,
            projectId: config.projectId,
            authDomain: config.authDomain,
            databaseURL: config.databaseURL,
          });

          // Initialize app first
          const app = initializeApp(config);
          console.log('✅ [FIREBASE] Firebase app initialized');
          
          // Initialize Firestore without any caching - direct backend only
          // This ensures all data comes directly from Firestore, no local cache
          try {
            const { 
              initializeFirestore: firebaseInitializeFirestore,
            } = require('firebase/firestore');
            
            // The KEY: use minimal settings to allow WebSocket connection
            // DON'T force long polling - it prevents WebSocket from being established
            // Just disable local caching to force direct backend access
            const firestoreInstance = firebaseInitializeFirestore(app, {
              // Don't specify any network settings - use Firebase defaults
              // This allows WebSocket to connect naturally
              // Don't specify localCache - this prevents any local persistence
            });
            
            console.log('✅ [FIREBASE] Firestore initialized - DIRECT BACKEND MODE (WebSocket enabled)');
            console.log('✅ [FIREBASE] Using native WebSocket for real-time updates');
          } catch (firestoreError) {
            console.warn('⚠️ [FIREBASE] Firestore initialization failed:', firestoreError);
            // Continue anyway - will use fallback
          }
          
          console.log('✅ [FIREBASE] Firebase Web SDK initialized successfully');
        }
      } catch (webError) {
        console.warn('⚠️ [FIREBASE] Firebase Web SDK initialization failed, using stubs only:', webError);
        console.log('ℹ️ [FIREBASE] Chat will work with offline/mock data only');
      }
    } else {
      console.log('📱 [FIREBASE] Using React Native Firebase (automatic initialization)');
      // React Native Firebase initializes automatically
    }

    isInitialized = true;
    console.log('✅ [FIREBASE] Firebase initialization complete');
    return true;
  } catch (error) {
    initializationError = error as Error;
    console.error('❌ [FIREBASE] Firebase initialization failed:', error);
    isInitialized = true; // Mark as initialized even on error to prevent retries
    return false;
  }
};

/**
 * Check if Firebase is properly initialized
 */
export const isFirebaseInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Get initialization error if any
 */
export const getInitializationError = (): Error | null => {
  return initializationError;
};

/**
 * Detect platform
 */
export const getPlatform = () => {
  return {
    isWeb: isWebPlatform(),
    isMobile: !isWebPlatform(),
    platform: isWebPlatform() ? 'web' : 'native',
  };
};

// Auto-initialize when module loads (web only)
if (typeof window !== 'undefined' && !isInitialized) {
  // Defer initialization to allow time for environment variables to load
  // Increased from 100ms to 500ms to ensure Firebase initializes before ChatView mounts
  const timer = setTimeout(() => {
    console.log('🔥 [FIREBASE] Auto-initialization timeout fired, starting initialization...');
    initializeFirebase().catch(error => {
      console.error('Auto-initialization failed:', error);
    });
    
    // Run diagnostics after a delay
    setTimeout(() => {
      FirebaseDiagnostics.printDiagnostics().catch(e => {
        console.error('Diagnostics failed:', e);
      });
    }, 2000);
  }, 500);

  // Cleanup on unmount
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('beforeunload', () => clearTimeout(timer));
  }

  // Expose diagnostics globally for manual debugging
  FirebaseDiagnostics.exposeGlobal();
}

export default {
  getFirestore,
  getFirestoreInstance,
  getAuth,
  getMessaging,
  initializeFirebase,
  isFirebaseInitialized,
  getInitializationError,
  getPlatform,
  isWebPlatform,
};
